/**
 * ⭐ **The handle on the live channel the tablet holds now** [03-us-kad-6j-i] — for the welcome's tap.
 *
 * The channel lives inside `useLiveStatusSocket`'s effect, and the welcome, which needs it at *Tap to start*,
 * has no path to it. So the hook HOLDS it here while the tablet is enrolled and releases it on the way out, and
 * the welcome asks through this module. It is a file of its own because it is a different job from the
 * channel's: `liveSocket.ts` is the socket and the end ledger; this is who can reach them, and the one line a
 * visit started without a confirmation writes [0521].
 *
 * ⛔ Module state for the same reason the 401 listener is (`unauthenticated.ts`): a handle, not a verdict, and
 * nothing renders from it.
 */
import type { LiveChannel } from './liveSocket';
import type { VisitEndCause } from './visitEnd';

let held: LiveChannel | undefined;

/**
 * What the tap's wait came to. A `timedOut` wait carries the channel that timed out, so the visit it starts
 * is counted on THAT channel, whatever is held by then [review round 1].
 */
export type TapWait =
  | { readonly outcome: 'confirmed' }
  | {
      readonly outcome: 'timedOut';
      readonly cause: VisitEndCause;
      readonly channel: LiveChannel;
    }
  | { readonly outcome: 'abandoned' };

/** The `timedOut` wait, the only one a fallback is counted from. */
export type TimedOutWait = Extract<TapWait, { readonly outcome: 'timedOut' }>;

/**
 * Makes `channel` the one {@link confirmTheLastVisitEnd} asks, and returns its release. The release clears only
 * its OWN channel, defensively: React runs an effect's cleanup before the next setup, so in this app the first
 * holder is always released before a second holds — the check keeps a late release from ever dropping a newer
 * channel.
 */
export function holdLiveChannel(channel: LiveChannel): () => void {
  held = channel;
  return () => {
    if (held === channel) held = undefined;
  };
}

/**
 * ⭐ **The tap's wait for the last visit's end** [03-us-kad-6j-i] — `confirmBeforeStart` on the channel the
 * tablet holds.
 *
 * ⚠️ **With no channel held, there is nothing to wait for, and that is said, not swallowed.** The welcome renders
 * only on an enrolled tablet, where `useLiveStatusSocket` holds one; reaching here without it is a defect in
 * the wiring, so one `console.error` names it and the tap goes on — a journey held for an end nobody can send
 * would be a dead end [BR-K7]. ⛔ Nothing identifying in the line.
 */
export async function confirmTheLastVisitEnd(visitNumber: number | undefined): Promise<TapWait> {
  const channel = held;
  if (channel === undefined) {
    console.error({
      msg: 'Tap to start found no live channel held — the last visit’s end could not be confirmed or sent',
      alert: 'visit_end_channel_missing',
    });
    return { outcome: 'confirmed' };
  }
  const wait = await channel.confirmBeforeStart(visitNumber);
  return wait.outcome === 'timedOut' ? { ...wait, channel } : wait;
}

/**
 * ⭐ **A visit started without its previous visit's end confirmed leaves one line, at once** [0521; 03-us-kad-6j-i
 * AC-6, AC-14], and the channel that timed out counts it for its next end call. During that visit the server
 * may still hold the last one open, so the four paths 0437 and 0449 describe are open again [0509 Ruling 4].
 *
 * `error`, as 0521 rules: the api logs the same event on the next end call, at error level, where its alert
 * rules read it [0308]. `cause` is why the last end call was not confirmed — a status, `network` or `aborted`.
 * ⛔ Nothing identifying: no candidate, no `kioskId`, no venue.
 */
export function visitStartedUnconfirmed(wait: TimedOutWait): void {
  reportVisitStartedUnconfirmed(wait.channel.startedUnconfirmed(), wait.cause);
}

function reportVisitStartedUnconfirmed(unconfirmedStarts: number, cause: VisitEndCause): void {
  console.error({
    msg: 'a visit started with the previous visit’s end unconfirmed — the api did not confirm it within 5 s of the tap',
    alert: 'visit_started_unconfirmed',
    unconfirmedStarts,
    cause,
  });
}
